import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, RotateCcw, Plus, Loader2, Bot, User } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useEntity } from "@/lib/entity";

const newSessionId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const SUGGESTIONS = [
  "Synthétise la situation financière de mon entité courante",
  "Quelles sont les bonnes pratiques OHADA pour clôturer un exercice ?",
  "Aide-moi à rédiger un mémo stratégique pour le conseil d'administration",
  "Propose 3 KPIs pertinents pour piloter une station-service",
  "Analyse les risques HSE d'une centrale d'achat",
];

export default function AIPanel({ open, onClose }) {
  const { user } = useAuth();
  const { currentEntity } = useEntity();
  const [sessionId, setSessionId] = useState(() => newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId,
        message,
        entity_id: currentEntity?.id,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `⚠️ Erreur: ${e?.response?.data?.detail || e.message}`, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    setSessionId(newSessionId());
    setMessages([]);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-obsidian-950/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed right-0 top-0 bottom-0 w-full lg:w-aipanel bg-obsidian-900 border-l border-line z-50 flex flex-col"
            data-testid="ai-panel"
          >
            {/* Header */}
            <div className="h-topbar flex items-center justify-between px-5 border-b border-line">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 grid place-items-center bg-citron rounded-sm">
                  <Sparkles size={14} className="text-obsidian-950" />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-tight">Assistant IA Direction</div>
                  <div className="text-[10px] text-obsidian-400 font-mono">Claude Sonnet 4.5</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={resetChat}
                  className="btn-ghost h-8 w-8 p-0"
                  title="Nouvelle conversation"
                  data-testid="ai-new-session-button"
                >
                  <Plus size={14} />
                </button>
                <button
                  onClick={onClose}
                  className="btn-ghost h-8 w-8 p-0"
                  title="Fermer"
                  data-testid="ai-close-button"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Conversation */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-5">
                  <div className="overline text-citron">Bonjour {user?.full_name?.split(" ")[0] || ""}</div>
                  <p className="text-sm text-obsidian-300 leading-relaxed">
                    Je suis votre copilote IA exécutif. Posez-moi vos questions sur les finances, les RH, la stratégie, l'OHADA — ou demandez-moi de rédiger un document, synthétiser des données, identifier des anomalies.
                  </p>
                  <div className="space-y-2 pt-2">
                    <div className="overline">Suggestions</div>
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s)}
                        className="w-full text-left p-3 bg-obsidian-850 hover:bg-obsidian-800 border border-line hover:border-citron/40 rounded-sm text-xs text-obsidian-200 transition-colors"
                        data-testid={`ai-suggestion-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <MessageBubble key={i} m={m} />
              ))}

              {loading && (
                <div className="flex items-center gap-2 text-xs text-obsidian-400">
                  <Loader2 size={14} className="animate-spin text-citron" />
                  <span>L'assistant réfléchit…</span>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-4 border-t border-line"
            >
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Posez votre question à l'IA…"
                  rows={1}
                  className="input min-h-[40px] max-h-32 resize-none py-2.5 leading-snug"
                  data-testid="ai-chat-input"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="btn-primary h-10 w-10 p-0 flex-shrink-0"
                  data-testid="ai-send-button"
                >
                  <Send size={14} />
                </button>
              </div>
              <div className="mt-2 text-[10px] text-obsidian-500 font-mono">
                Entrée pour envoyer · Shift+Entrée pour nouvelle ligne · Contexte: {currentEntity?.name}
              </div>
            </form>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`w-7 h-7 flex-shrink-0 grid place-items-center rounded-sm ${
          isUser ? "bg-obsidian-700 text-obsidian-100" : "bg-citron text-obsidian-950"
        }`}
      >
        {isUser ? <User size={13} /> : <Bot size={13} />}
      </div>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-sm text-sm leading-relaxed ${
          isUser
            ? "bg-obsidian-800 text-obsidian-50"
            : m.error
              ? "bg-bad/10 border border-bad/30 text-bad"
              : "bg-obsidian-850 border border-line text-obsidian-100"
        }`}
      >
        <div className="whitespace-pre-wrap break-words">{m.content}</div>
      </div>
    </div>
  );
}
